/**
 * La cola de Ingesta: cómo se agrupa y se rotula lo que se ha ingerido.
 *
 * Vive aquí y no en la página porque decide algo que hay que poder fijar con
 * tests: que un libro Excel NO se rotule «pendiente de extracción». Comparte
 * con un PDF sin extraer el no tener `fecha_operacion`, pero por un motivo
 * OPUESTO — al PDF le falta pasar por Gemini, y el libro es mensual y no tiene
 * una sola jornada que poner.
 */
import type { Ingesta as IngestaRow } from './api.js';

/** Sentinel group key for rows whose report date isn't known yet (pre-extracción). */
export const SIN_FECHA = '__sin_fecha__';

/**
 * Los libros Excel van a su propio grupo. Comparten con los PDF el no tener
 * `fecha_operacion`, pero por un motivo OPUESTO: al PDF le falta extraerse, y
 * el libro es mensual y no tiene una sola jornada. Meterlos juntos rotularía
 * «pendiente de extracción» sobre libros que ya están importados.
 */
export const LIBROS = '__libros__';

/** Los tipos que entran por Excel y se importan al momento, sin cola. */
export const TIPOS_LIBRO = new Set(['cuentas', 'expedia']);

/** Rótulo corto de cada tipo, para la columna «Reporte» de la cola. */
export const TIPO_BADGE: Record<string, { label: string; tone: string }> = {
  flash: { label: 'Manager-Flash', tone: 'bg-slate-700 text-slate-200' },
  diamond: { label: 'Res. Stat 2', tone: 'bg-violet-900 text-violet-200' },
  source: { label: 'Source · mensual', tone: 'bg-sky-900 text-sky-200' },
  roomtype: { label: 'Tipo de hab.', tone: 'bg-teal-900 text-teal-200' },
  cuentas: { label: 'Cuentas (Excel)', tone: 'bg-amber-900 text-amber-200' },
  expedia: { label: 'Expedia (Excel)', tone: 'bg-fuchsia-900 text-fuchsia-200' },
};

/**
 * Una línea con lo que dejó el import de un libro: sale del `resumen` que
 * guardó el Worker, que es el MISMO objeto que la respuesta del import.
 */
export function resumenLibro(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const r = JSON.parse(raw) as {
      imported?: number;
      years?: number[];
      sedes?: number;
      cuentas?: number;
      noches?: number;
      reservas?: number;
      issues?: string[];
    };
    const partes = [`${r.imported ?? 0} fila(s)`];
    if (r.years?.length) partes.push(r.years.join(', '));
    if (r.sedes) partes.push(`${r.sedes} sede(s)`);
    if (r.cuentas) partes.push(`${r.cuentas} cuenta(s)`);
    if (r.noches) partes.push(`${r.noches} noche(s)`);
    if (r.reservas) partes.push(`${r.reservas} reserva(s)`);
    if (r.issues?.length) partes.push(`${r.issues.length} aviso(s)`);
    return partes.join(' · ');
  } catch {
    return null;
  }
}

const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Friendly Spanish label for a 'YYYY-MM-DD' report date (UTC parse — date-only). */
export function fechaLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${DIAS[dt.getUTCDay()]} ${d} ${MESES[m - 1]} ${y}`;
}

/**
 * Agrupa por `fecha_operacion` (jornada del reporte). Los libros Excel van a su
 * propio grupo y los PDF sin extraer, al de pendientes.
 */
export function groupByFecha(rows: IngestaRow[]): { key: string; rows: IngestaRow[] }[] {
  const map = new Map<string, IngestaRow[]>();
  for (const r of rows) {
    const key = TIPOS_LIBRO.has(r.tipo) ? LIBROS : (r.fecha_operacion ?? SIN_FECHA);
    const arr = map.get(key) ?? [];
    arr.push(r);
    map.set(key, arr);
  }
  // Pendientes primero (piden acción), luego los libros, luego las fechas de
  // más reciente a más antigua.
  const orden = (k: string) => (k === SIN_FECHA ? 0 : k === LIBROS ? 1 : 2);
  return [...map.entries()]
    .sort(([a], [b]) => {
      if (orden(a) !== orden(b)) return orden(a) - orden(b);
      return a < b ? 1 : a > b ? -1 : 0;
    })
    .map(([key, groupRows]) => ({ key, rows: groupRows }));
}
